/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import get from 'lodash/get';
import has from 'lodash/has';
import fetch from '../fetch';
import moment from 'moment';
import profileStore from './profileStore';
import crypto from 'crypto';
import ab2b from 'arraybuffer-to-buffer';

class S3MultiUpload {
    constructor(
        file,
        {
            chunkSize = 10 * 1024 * 1024, //10mb
            parallelUploads = 3,
        } = {}
    ) {
        this.PART_SIZE = chunkSize; // Minimum part size defined by aws s3 is 5 MB, maximum 5 GB
        this.completed = false;
        this.completedArray = [];
        this.file = file;
        this.parallelUploads = parallelUploads;

        const { type, size, lastModifiedDate } = this.file;

        const client = profileStore.client._id;
        const date = moment().format('DDMMYYYY');
        const hour = moment().format('HH');
        const minute = moment().format('mm');

        const keyName = new Date().valueOf();

        this.fileInfo = {
            fileKey: `videos/${client}/${date}/${hour}/${minute}/${keyName}`,
            ContentType: type,
            keyName,
            type,
            size,
            lastModifiedDate,
        };

        this.sendBackData = null;
        this.uploadXHR = [];
        // Progress monitoring
        this.byterate = [];
        this.lastUploadedSize = [];
        this.lastUploadedTime = [];
        this.loaded = [];
        this.total = [];

        this.start = this.start.bind(this);
        this.abort = this.abort.bind(this);
        this.create = this.create.bind(this);
        this.upload = this.upload.bind(this);
        this.complete = this.complete.bind(this);
        this.uploadSinglePart = this.uploadSinglePart.bind(this);
        this.sendToS3 = this.sendToS3.bind(this);
        this.updateProgress = this.updateProgress.bind(this);
    }

    async start({ onProgressChanged = false }) {
        this.handlers = {
            onProgressChanged,
        };
        const res = await this.create();
        if (has(res, 'Key')) return res.Key;
        else throw res;
    }

    async abort() {
        for (let i = 0; i < this.uploadXHR.length; i++) {
            this.uploadXHR[i].abort();
        }
        return await fetch.post({
            url: '/api/assets/s3/abort-multipart/',
            body: {
                fileKey: this.fileInfo.fileKey,
                UploadId: this.uploadId,
            },
            disableClientFilter: true,
        });
    }

    async create() {
        const data = await fetch.post({
            url: '/api/assets/s3/get-multipart/',
            body: this.fileInfo,
            disableClientFilter: true,
        });
        let uploadId = get(data, 'UploadId');
        if (!uploadId || data.error) throw data;
        this.uploadId = uploadId;
        this.sendBackData = data;
        return await this.upload();
    }

    async blobToMD5(blob) {
        return new Promise((resolve, reject) => {
            let fileReader = new FileReader();
            fileReader.readAsArrayBuffer(blob);
            fileReader.onloadend = () => {
                const str = ab2b(fileReader.result);
                var hash = crypto
                    .createHash('md5')
                    .update(str)
                    .digest('base64');
                resolve(hash);
            };
        });
    }

    async upload() {
        let blobs = (this.blobs = []);
        let start = 0;
        let end;
        let partNum = 0;

        while (start < this.file.size) {
            end = Math.min(start + this.PART_SIZE, this.file.size);
            let filePart = this.file.slice(start, end);
            // this is to prevent push blob with 0Kb
            if (filePart.size > 0) blobs.push(filePart);
            start = this.PART_SIZE * ++partNum;
        }

        for (let i = 0; i < blobs.length; i += this.parallelUploads) {
            let requests = [];
            for (let j = i; j < i + this.parallelUploads; j++) {
                if (!blobs[j]) break;
                requests.push(this.uploadSinglePart(blobs[j], j + 1));
            }
            if (requests.length > 0) await Promise.all(requests);
        }
        const response = this.complete();
        return response;
    }

    async uploadSinglePart(blob, partNumber) {
        const hash = await this.blobToMD5(blob);
        let res = await fetch.post({
            url: '/api/assets/s3/presign-part/',
            body: {
                keyPath: '/' + this.fileInfo.fileKey,
                uploadId: this.uploadId,
                partNumber,
                contentMD5: hash,
            },
            disableClientFilter: true,
        });
        if (res.error) {
            throw res.error;
        }
        return await this.sendToS3(res, blob, partNumber);
    }

    async complete() {
        if (this.completed) return;

        this.completed = true;

        const data = await fetch.post({
            url: '/api/assets/s3/complete-multipart-upload/',
            body: {
                fileKey: this.fileInfo.fileKey,
                UploadId: this.uploadId,
                Parts: this.completedArray,
            },
            disableClientFilter: true,
        });
        if (!data || data.error) throw data;
        return data;
    }

    async sendToS3(data, blob, partNumber) {
        return new Promise((resolve, reject) => {
            const index = partNumber - 1;
            let { url, headers } = data;
            let size = blob.size;
            let request = (this.uploadXHR[index] = new XMLHttpRequest());

            request.onreadystatechange = () => {
                if (request.readyState === 4) {
                    // 4 is DONE
                    // self.uploadXHR[index] = null;
                    if (request.status !== 200) {
                        this.updateProgress();
                        reject(request);
                        return;
                    }

                    this.completedArray[index] = {
                        ETag: request.getResponseHeader('ETag'),
                        PartNumber: partNumber,
                    };
                    this.updateProgress();
                    resolve();
                }
            };

            request.upload.onprogress = e => {
                if (e.lengthComputable) {
                    this.total[index] = size;
                    this.loaded[index] = e.loaded;
                    if (this.lastUploadedTime[index]) {
                        var time_diff = (new Date().getTime() - this.lastUploadedTime[index]) / 1000;
                        if (time_diff > 0.005) {
                            // 5 miliseconds has passed
                            var byterate = (this.loaded[index] - this.lastUploadedSize[index]) / time_diff;
                            this.byterate[index] = byterate;
                            this.lastUploadedTime[index] = new Date().getTime();
                            this.lastUploadedSize[index] = this.loaded[index];
                        }
                    } else {
                        this.byterate[index] = 0;
                        this.lastUploadedTime[index] = new Date().getTime();
                        this.lastUploadedSize[index] = this.loaded[index];
                    }
                    // Only send update to user once, regardless of how many
                    // parallel XHRs we have (unless the first one is over).
                    if (index === 0 || this.total[0] === this.loaded[0]) this.updateProgress();
                }
            };

            request.open('PUT', url, true);
            request.setRequestHeader('Content-MD5', headers['Content-MD5']);
            request.setRequestHeader('Authorization', headers['Authorization']);
            request.setRequestHeader('X-Amz-Content-Sha256', headers['X-Amz-Content-Sha256']);
            request.setRequestHeader('X-Amz-Date', headers['X-Amz-Date']);
            request.send(blob);
        });
    }

    updateProgress = () => {
        let total = 0;
        let loaded = 0;
        let byterate = 0.0;

        for (let i = 0; i < this.total.length; i++) {
            loaded += +this.loaded[i] || 0;
            total += this.total[i];
            if (this.loaded[i] !== this.total[i]) {
                // Only count byterate for active transfers
                byterate += +this.byterate[i] || 0;
            }
        }
        total = this.fileInfo.size;
        this.onProgressChanged(loaded, total, byterate);
    };

    /*events*/
    onProgressChanged = (uploadedSize, totalSize, bitrate) => {
        if (this.handlers.onProgressChanged) this.handlers.onProgressChanged(uploadedSize, totalSize, bitrate);
    };
}

export default S3MultiUpload;
